// ---------------------------------------------------------------------------
// Digital Notice Board — SlideTimeline (bottom studio strip)
// ---------------------------------------------------------------------------
// The video-editor-style scene strip. Each slide is a live mini-render (same
// ElementVisual, tiny scale) so the thumbnail always matches the real screen.
// Users add, duplicate, delete, reorder and switch the active slide here. The
// projection player walks these left→right, honouring each slide's duration.
// ---------------------------------------------------------------------------
import React, {useEffect, useRef, useState} from "react";
import {Box, Flex, HStack, IconButton, Text, useColorModeValue} from "@chakra-ui/react";
import {FaChevronLeft, FaChevronRight, FaClone, FaPlus, FaTrash} from "react-icons/fa";
import ElementVisual from "./ElementVisual";

const THUMB_W = 150;

function SlideThumb({slide, canvas, active, index, onSelect}) {
    const ref = useRef(null);
    const [scale, setScale] = useState(THUMB_W / canvas.width);

    useEffect(() => {
        if (ref.current) setScale(ref.current.clientWidth / canvas.width);
    }, [canvas.width]);

    const height = canvas.height * scale;

    return (
        <Box
            onClick={() => onSelect(index)}
            cursor="pointer"
            border={active ? "2px solid #3182CE" : "2px solid transparent"}
            borderRadius="8px"
            overflow="hidden"
            flexShrink={0}
        >
            <Box
                ref={ref}
                position="relative"
                w={`${THUMB_W}px`}
                h={`${height}px`}
                bg={slide.background}
                overflow="hidden"
            >
                {slide.elements.map((el) => (
                    <Box
                        key={el.id}
                        position="absolute"
                        left={`${el.x * scale}px`}
                        top={`${el.y * scale}px`}
                        w={`${el.w * scale}px`}
                        h={`${el.h * scale}px`}
                        transform={el.rotation ? `rotate(${el.rotation}deg)` : undefined}
                        pointerEvents="none"
                    >
                        <ElementVisual element={el} scale={scale} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default function SlideTimeline({
    slides,
    canvas,
    activeIndex,
    onSelect,
    onAdd,
    onDuplicate,
    onDelete,
    onMove,
}) {
    const labelColor = useColorModeValue("gray.600", "gray.200");
    const strip = useColorModeValue("gray.50", "gray.800");

    return (
        <Box bg={strip} borderRadius="14px" p="12px">
            <Flex justify="space-between" align="center" mb="8px">
                <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.5px">
                    TIMELINE · {slides.length} SLIDE{slides.length !== 1 ? "S" : ""}
                </Text>
                <IconButton size="xs" colorScheme="blue" icon={<FaPlus />} aria-label="Add slide" onClick={onAdd} />
            </Flex>

            <HStack spacing="12px" overflowX="auto" pb="6px" align="flex-start">
                {slides.map((slide, i) => (
                    <Box key={slide.id}>
                        <SlideThumb
                            slide={slide}
                            canvas={canvas}
                            active={i === activeIndex}
                            index={i}
                            onSelect={onSelect}
                        />
                        <Flex justify="space-between" align="center" mt="4px" px="2px">
                            <Text fontSize="10px" color={labelColor} isTruncated maxW="70px">
                                {i + 1}. {slide.name}
                            </Text>
                            <HStack spacing="1px">
                                <IconButton size="xs" variant="ghost" aria-label="Left" icon={<FaChevronLeft />} onClick={() => onMove(i, i - 1)} />
                                <IconButton size="xs" variant="ghost" aria-label="Right" icon={<FaChevronRight />} onClick={() => onMove(i, i + 1)} />
                                <IconButton size="xs" variant="ghost" aria-label="Duplicate" icon={<FaClone />} onClick={() => onDuplicate(i)} />
                                <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    aria-label="Delete"
                                    icon={<FaTrash />}
                                    isDisabled={slides.length <= 1}
                                    onClick={() => onDelete(i)}
                                />
                            </HStack>
                        </Flex>
                    </Box>
                ))}
            </HStack>
        </Box>
    );
}
